import React, { useState, useEffect } from 'react';
import {
  Table,
  Spin,
  Button,
  Drawer,
  List,
  Modal,
  Checkbox,
  Input,
  Select,
  message,
} from 'antd';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import { arrayMoveImmutable as arrayMove } from 'array-move';
import { useSelector } from 'react-redux';
import AxiosOICStat from 'AxiosOICStat';
import { getIndicatorName } from '../utils/indicatorName';

const { Option } = Select;

const DragHandle = SortableHandle(() => (
  <span style={{ cursor: 'grab', color: '#999' }}>≡</span>
));

const SortableItem = SortableElement((props) => <tr {...props} />);
const SortableBody = SortableContainer((props) => <tbody {...props} />);

function GenerateContentPage() {
  const indicatorsState = useSelector((state) => state.indicators);
  const categoriesState = useSelector((state) => state.categories);

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [orderData, setOrderData] = useState([]);

  const [modalVisible, setModalVisible] = useState(false);
  const [generateTOC, setGenerateTOC] = useState(false);
  const [generateLOT, setGenerateLOT] = useState(false);

  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");

  const [footerText, setFooterText] = useState("");
  const [footerFontSize, setFooterFontSize] = useState("");
  const [headerTextForTOC, setHeaderTextForTOC] = useState("");
  const [headerTextForLOT, setHeaderTextForLOT] = useState("");
  const [alignment, setAlignment] = useState("");
  const [indentationMargin, setIndentationMargin] = useState("");
  const [fontFamily, setFontFamily] = useState("");
  const [colorForTocLot, setColorForTocLot] = useState("");
  const [isOrderFetched, setIsOrderFetched] = useState(false);

  const handleGeneratePages = () => {
    if (orderData.length === 0) {
      message.error("Please get the order first.");
      return;
    }
    const payload = {
      orderData,
      generateTOC,
      generateLOT,
    };
    if (footerText) payload.footer_text = footerText;
    if (footerFontSize) payload.footer_font_size = Number(footerFontSize);
    if (headerTextForTOC) payload.header_text_for_TOC = headerTextForTOC;
    if (headerTextForLOT) payload.header_text_for_LOT = headerTextForLOT;
    if (alignment) payload.alignment = alignment;
    if (indentationMargin) payload.indentation_margin = Number(indentationMargin);
    if (fontFamily) payload.font_family = fontFamily;
    if (colorForTocLot) payload.color_for_TOC_LOT = colorForTocLot;

    AxiosOICStat.post('/syb/generate_table_of_contents_pdf/', payload, {
      responseType: 'blob',
    })
      .then((response) => {
        const file = new Blob([response.data], { type: 'application/pdf' });
        const fileURL = URL.createObjectURL(file);
        setPreviewUrl(fileURL);
        setPreviewModalVisible(true);
      })
      .catch((error) => {
        console.error('Error generating pages:', error);
      });

    setModalVisible(false);
  };

  const handleGenerateWord = () => {
    if (orderData.length === 0) {
      message.error("Please get the order first.");
      return;
    }
    const payload = {
      orderData,
      generateTOC,
      generateLOT,
    };

    console.log("Generate Word payload: ", payload);

    if (footerText) payload.footer_text = footerText;
    if (footerFontSize) payload.footer_font_size = Number(footerFontSize);
    if (headerTextForTOC) payload.header_text_for_TOC = headerTextForTOC;
    if (headerTextForLOT) payload.header_text_for_LOT = headerTextForLOT;
    if (alignment) payload.alignment = alignment;
    if (indentationMargin) payload.indentation_margin = Number(indentationMargin);
    if (fontFamily) payload.font_family = fontFamily;
    if (colorForTocLot) payload.color_for_TOC_LOT = colorForTocLot;

    AxiosOICStat.post('/syb/generate_table_of_contents_docx/', payload, {
      responseType: 'blob',
    })
      .then((response) => {
        // Trigger file download for DOCX
        const file = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        });
        const fileURL = URL.createObjectURL(file);
        const link = document.createElement('a');
        link.href = fileURL;
        link.download = 'toc_lot_report.docx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch((error) => {
        console.error('Error generating Word document:', error);
      });
    setModalVisible(false);
  };


  useEffect(() => {
    AxiosOICStat.get('/syb/syb_categories/')
      .then((response) => {
        const categoryMap = {};
        response.data.forEach(([categoryCode, indicatorCode]) => {
          if (!categoryMap[categoryCode]) {
            categoryMap[categoryCode] = {
              categoryCode,
              categoryName:
                categoriesState.value[categoryCode]?.cat_name_eng ||
                `Category ${categoryCode}`,
              indicators: [],
            };
          }
          categoryMap[categoryCode].indicators.push({
            indicatorCode,
            indicatorName:
              getIndicatorName(indicatorsState.value[indicatorCode]) ||
              `Indicator ${indicatorCode}`,
          });
        });
        const categoriesArray = Object.values(categoryMap);
        setCategories(categoriesArray);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching categories:', error);
        setLoading(false);
      });
  }, [categoriesState, indicatorsState]);

  const onSortCategoriesEnd = ({ oldIndex, newIndex }) => {
    if (oldIndex !== newIndex) {
      setCategories((prev) => arrayMove(prev, oldIndex, newIndex));
    }
  };

  const DraggableCategoryBody = (props) => (
    <SortableBody
      useDragHandle
      disableAutoscroll
      helperClass="row-dragging"
      onSortEnd={onSortCategoriesEnd}
      {...props}
    />
  );

  const DraggableCategoryRow = ({ className, style, ...restProps }) => {
    const index = categories.findIndex(
      (cat) => `cat_${cat.categoryCode}` === restProps['data-row-key']
    );
    return <SortableItem index={index} {...restProps} />;
  };

  const ExpandedRowTable = ({ indicators, categoryIndex }) => {
    const [localIndicators, setLocalIndicators] = useState(indicators);

    useEffect(() => {
      setLocalIndicators(indicators);
    }, [indicators]);

    const onSortIndicatorsEnd = ({ oldIndex, newIndex }) => {
      if (oldIndex !== newIndex) {
        const newIndicators = arrayMove(localIndicators, oldIndex, newIndex);
        setLocalIndicators(newIndicators);
        setCategories((prev) => {
          const updated = [...prev];
          updated[categoryIndex] = {
            ...updated[categoryIndex],
            indicators: newIndicators,
          };
          return updated;
        });
      }
    };

    const DraggableIndicatorBody = (props) => (
      <SortableBody
        useDragHandle
        disableAutoscroll
        helperClass="row-dragging"
        onSortEnd={onSortIndicatorsEnd}
        {...props}
      />
    );

    const DraggableIndicatorRow = ({ className, style, ...restProps }) => {
      const index = localIndicators.findIndex(
        (ind) => `ind_${ind.indicatorCode}` === restProps['data-row-key']
      );
      return <SortableItem index={index} {...restProps} />;
    };

    const indicatorColumns = [
      {
        title: '',
        dataIndex: 'sort',
        width: 30,
        render: () => <DragHandle />,
      },
      {
        title: '',
        dataIndex: 'indicatorName',
      },
    ];

    const indicatorDataSource = localIndicators.map((ind) => ({
      key: `ind_${ind.indicatorCode}`,
      ...ind,
    }));

    return (
      <Table
        columns={indicatorColumns}
        dataSource={indicatorDataSource}
        pagination={false}
        rowKey="key"
        components={{
          body: {
            wrapper: DraggableIndicatorBody,
            row: DraggableIndicatorRow,
          },
        }}
      />
    );
  };

  const categoryColumns = [
    {
      title: '',
      dataIndex: 'sort',
      width: 30,
      render: () => <DragHandle />,
    },
    {
      title: 'Category',
      dataIndex: 'categoryName',
      render: (text) => <strong>{text}</strong>,
    },
  ];

  const categoryDataSource = categories.map((cat, catIndex) => ({
    key: `cat_${cat.categoryCode}`,
    ...cat,
  }));

  const reorderCategories = (categories, orderData) => {
    const orderMap = {};
    orderData.forEach((ord) => {
      orderMap[ord.categoryCode] = ord;
    });

    const sortedCategories = [...categories].sort((a, b) => {
      const orderA = orderMap[a.categoryCode]?.order || '0';
      const orderB = orderMap[b.categoryCode]?.order || '0';
      return parseFloat(orderA) - parseFloat(orderB);
    });

    sortedCategories.forEach((cat) => {
      const ordCat = orderMap[cat.categoryCode];
      if (ordCat && ordCat.indicators) {
        const indicatorOrderMap = {};
        ordCat.indicators.forEach((ind) => {
          indicatorOrderMap[ind.indicatorCode] = ind.order;
        });
        cat.indicators.sort((indA, indB) => {
          const orderA = indicatorOrderMap[indA.indicatorCode] || '0';
          const orderB = indicatorOrderMap[indB.indicatorCode] || '0';
          return parseFloat(orderA) - parseFloat(orderB);
        });
      }
    });
    return sortedCategories;
  };

  const getTableOrder = () => {
    AxiosOICStat.get('/syb/order-data')
      .then((response) => {
        const orderDataFromEndpoint = response.data.orderData
          ? response.data.orderData
          : response.data;
        setOrderData(orderDataFromEndpoint);

        //fixed the race condition ß
        setCategories(prev =>
          prev.length ? reorderCategories(prev, orderDataFromEndpoint) : prev
        );
        setDrawerVisible(true);
        //message.info('Retrieved updated order data.');
      })
      .catch((error) => {
        message.error('Error retrieving order data.');
        console.error('Error retrieving order data:', error);
      });
  };

  const setTableOrder = () => {
    const order = categories.map((cat, catIndex) => ({
      order: `${catIndex + 1}.`,
      categoryCode: cat.categoryCode,
      categoryName: cat.categoryName,
      indicators: cat.indicators.map((ind, indIndex) => ({
        order: `${catIndex + 1}.${indIndex + 1}`,
        indicatorCode: ind.indicatorCode,
        indicatorName: ind.indicatorName,
      })),
    }));

    setOrderData(order);
    console.log('Table Order:', order);

    AxiosOICStat.post('/syb/order-data', { orderData: order })
      .then((response) => {
        message.success('Order data saved successfully.');
      })
      .catch((error) => {
        message.error('Error saving order data.');
      });
  };

  return (
    <>
      <style>{`
        .row-dragging {
          font-family: inherit;
          font-size: inherit;
          color: inherit;
          background: #fafafa;
        }
      `}</style>
      {loading ? (
        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <Spin tip="Loading..." />
        </div>
      ) : (
        <>
          <Button
            onClick={() => setModalVisible(true)}
            style={{ marginBottom: 16, marginRight: 16 }}
          >
            Generate Content Page
          </Button>
          <Button onClick={getTableOrder} style={{ marginBottom: 16, marginRight: 16 }}>
            Get Order
          </Button>
          <Table
            columns={categoryColumns}
            dataSource={categoryDataSource}
            pagination={false}
            rowKey="key"
            expandable={{
              expandedRowRender: (record, index) => (
                <ExpandedRowTable indicators={record.indicators} categoryIndex={index} />
              ),
            }}
            expandedRowKeys={expandedRowKeys}
            onExpand={(expanded, record) => {
              const key = record.key;
              if (expanded) {
                setExpandedRowKeys([...expandedRowKeys, key]);
              } else {
                setExpandedRowKeys(expandedRowKeys.filter((k) => k !== key));
              }
            }}
            components={{
              body: {
                wrapper: DraggableCategoryBody,
                row: DraggableCategoryRow,
              },
            }}
          />
          <Drawer
            title="Table Order"
            placement="right"
            closable
            onClose={() => setDrawerVisible(false)}
            visible={drawerVisible}
            width={400}
          >
            <List
              dataSource={orderData}
              renderItem={(cat) => (
                <List.Item>
                  <div>
                    <strong>
                      {cat.order} {cat.categoryName} (Code: {cat.categoryCode})
                    </strong>
                    <List
                      size="small"
                      dataSource={cat.indicators}
                      renderItem={(ind) => (
                        <List.Item style={{ paddingLeft: 20 }}>
                          {ind.order} {ind.indicatorName} (Code: {ind.indicatorCode})
                        </List.Item>
                      )}
                    />
                  </div>
                </List.Item>
              )}
            />
          </Drawer>

          <Modal
            title="Which page do you want to generate?"
            visible={modalVisible}
            onCancel={() => setModalVisible(false)}
            width={600}
            footer={[
              <Button disabled={true} key="exportPdf" type="primary" onClick={handleGeneratePages}>
                Export as PDF
              </Button>,
              <Button key="exportWord" type="default" onClick={handleGenerateWord}>
                Export as Word
              </Button>,
              <Button key="cancel" onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
            ]}
          >
            <div style={{ marginBottom: 8 }}>
              <Checkbox
                checked={generateTOC}
                onChange={(e) => setGenerateTOC(e.target.checked)}
              >
                Table of Contents
              </Checkbox>
            </div>
            <div style={{ marginBottom: 8 }}>
              <Checkbox
                checked={generateLOT}
                onChange={(e) => setGenerateLOT(e.target.checked)}
              >
                List of Tables
              </Checkbox>
            </div>
            <div style={{ marginTop: 16, display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
              <div style={{ flex: '1 1 200px' }}>
                <label>Footer Text:</label>
                <Input
                  placeholder="Statistical Yearbook..."
                  value={footerText}
                  onChange={(e) => setFooterText(e.target.value)}
                />
              </div>
              <div style={{ flex: '1 1 200px' }}>
                <label>Footer Font Size:</label>
                <Input
                  type="number"
                  placeholder="11"
                  value={footerFontSize}
                  onChange={(e) => setFooterFontSize(e.target.value)}
                />
              </div>
              <div style={{ flex: '1 1 200px' }}>
                <label>Header Text for TOC:</label>
                <Input
                  placeholder="Table of Contents"
                  value={headerTextForTOC}
                  onChange={(e) => setHeaderTextForTOC(e.target.value)}
                />
              </div>
              <div style={{ flex: '1 1 200px' }}>
                <label>Header Text for LOT:</label>
                <Input
                  placeholder="List of Tables"
                  value={headerTextForLOT}
                  onChange={(e) => setHeaderTextForLOT(e.target.value)}
                />
              </div>
              <div style={{ flex: '1 1 200px' }}>
                <label>Alignment:</label>
                <Select
                  value={alignment}
                  onChange={(val) => setAlignment(val)}
                  style={{ width: '100%' }}
                  placeholder="L / C / R"
                >
                  <Option value="L">Left</Option>
                  <Option value="C">Center</Option>
                  <Option value="R">Right</Option>
                </Select>
              </div>
              <div style={{ flex: '1 1 200px' }}>
                <label>Indentation Margin:</label>
                <Input
                  type="number"
                  placeholder="20"
                  value={indentationMargin}
                  onChange={(e) => setIndentationMargin(e.target.value)}
                />
              </div>
              <div style={{ flex: '1 1 200px' }}>
                <label>Font Family:</label>
                <Input
                  placeholder="Arial"
                  value={fontFamily}
                  onChange={(e) => setFontFamily(e.target.value)}
                />
              </div>
              <div style={{ flex: '1 1 200px' }}>
                <label>Color for TOC/LOT (R,G,B):</label>
                <Input
                  placeholder="0,112,192"
                  value={colorForTocLot}
                  onChange={(e) => setColorForTocLot(e.target.value)}
                />
              </div>
            </div>
          </Modal>


          <Modal
            title="Preview PDF"
            visible={previewModalVisible}
            onCancel={() => setPreviewModalVisible(false)}
            footer={null}
            width={800}
          >
            <iframe
              src={previewUrl}
              style={{ width: '100%', height: '600px' }}
              title="PDF Preview"
            />
          </Modal>
        </>
      )}
    </>
  );
}

export default GenerateContentPage;
